import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useClient } from "../context/ClientContext";
import { PageHeader, Pill } from "../components/Primitives";
import { PaperPlaneRight, Sparkle, User, CircleNotch } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const SUGGESTIONS = [
  "Which workflow has the highest defect leakage risk this quarter?",
  "Why is SME dependency so high for Risk & Compliance?",
  "Recommend mitigations for the highest predictive-risk cluster.",
  "What scenarios should we add before signing off this release?",
];

export default function SmeAssistant() {
  const { activeClient, activeClientId } = useClient();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  // Reset chat when client changes
  useEffect(() => {
    if (!activeClient) return;
    setSessionId(null);
    setMessages([{
      role: "assistant",
      content: `I'm the VLink SME Assistant — currently advising on the ${activeClient.platform} platform at ${activeClient.name}. Ask me about workflow risks, defect clusters, regression strategy, or release confidence narratives.`,
    }]);
  }, [activeClient]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  async function send(text) {
    const message = (text ?? input).trim();
    if (!message) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }]);
    setBusy(true);
    try {
      const r = await api.post("/sme/chat", { session_id: sessionId, message, client_id: activeClientId });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: `Error: ${err?.response?.data?.detail || err.message}` }]);
    } finally { setBusy(false); }
  }

  return (
    <div className="h-full flex flex-col" data-testid="sme-page">
      <PageHeader
        overline={`AI · ${activeClient?.name || ""} / ${activeClient?.platform || ""}`}
        title="Quality Strategist"
        description="Senior QA strategist embedded in the platform — workflow-aware, defect-aware, release-aware."
        right={<Pill tone="ai" testid="sme-ai-pill"><Sparkle size={11} className="mr-1" /> Claude Sonnet 4.5</Pill>}
      />
      <div className="flex-1 grid grid-cols-12 min-h-0">
        <div className="col-span-12 lg:col-span-3 border-r border-zinc-200 bg-white p-6 overflow-y-auto" data-testid="sme-suggestions">
          <div className="overline text-zinc-500 mb-3">Try asking</div>
          <div className="space-y-2">
            {SUGGESTIONS.map((s, i) => (
              <button
                key={i}
                data-testid={`sme-suggestion-${i}`}
                onClick={() => send(s)}
                className="w-full text-left text-xs text-zinc-700 border border-zinc-200 hover:border-vlink-500 hover:bg-vlink-50 px-3 py-2.5 rounded-sm transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-9 flex flex-col min-h-0">
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-10 py-8 space-y-5" data-testid="sme-messages">
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {m.role === "assistant" && (
                  <div className="flex-shrink-0 w-7 h-7 border border-vlink-200 bg-vlink-50 flex items-center justify-center">
                    <Sparkle size={13} className="text-vlink-700" />
                  </div>
                )}
                <div
                  className={`max-w-[75%] text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-vlink-500 text-white px-4 py-3 rounded-sm"
                      : "text-zinc-900 px-4 py-3 border border-zinc-200 bg-white rounded-sm"
                  }`}
                >
                  {m.content}
                </div>
                {m.role === "user" && (
                  <div className="flex-shrink-0 w-7 h-7 border border-vlink-600 bg-vlink-600 text-white flex items-center justify-center">
                    <User size={13} />
                  </div>
                )}
              </motion.div>
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono">
                <CircleNotch size={12} className="animate-spin" /> THINKING…
              </div>
            )}
          </div>

          <div className="border-t border-zinc-200 bg-white px-10 py-4">
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-center gap-3"
              data-testid="sme-form"
            >
              <input
                data-testid="sme-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about a workflow, defect cluster, or release strategy…"
                disabled={busy}
                className="flex-1 border border-zinc-300 focus:border-vlink-500 outline-none rounded-sm px-4 py-3 text-sm bg-white"
              />
              <button
                data-testid="sme-send-btn"
                disabled={busy || !input.trim()}
                className="inline-flex items-center gap-2 bg-vlink-500 hover:bg-vlink-600 text-white text-sm font-medium px-4 py-3 rounded-sm transition-colors disabled:opacity-60"
              >
                Send <PaperPlaneRight size={13} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
